import type { InventoryItemRow } from "~/@types/inventoryItemRow";
import type { BatchPart, BatchQuantityInput, BatchView } from "~/helpers/inventoryBatches";
import type { LocationPathEntry } from "~/helpers/mobile/location/siteLocationAdapter";
import type { ReasonSelection } from "~/helpers/mobile/stickyReason";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	AttachAvailabilityPhotoInput,
	LocationStatus,
	type SetInventoryItemInput,
	SiteLocationType,
	SubLocationDocument
} from "~/gql/graphqlBase.generated";
import tenantChanged from "~/store/tenantChanged";
import { type RowShippingUnit } from "~fulfill/inventory/stock/helpers/rowShippingUnit";

/** A greenhouse location cached for offline use (legacy format for move picker). */
export interface AvailabilityLocation {
	locationId: string;
	name: string;
	path: string;
}

/** Minimal site info cached for offline drilldown. */
export interface CachedSiteInfo {
	erpId: string;
	name: string;
	siteLocationId: string;
	status?: LocationStatus;
}

/** Cached item for offline autocomplete. */
export interface CachedItemSummary {
	absorbedSkus?: string[];
	absorbedUpcs?: string[];
	batchTracked?: boolean;
	itemId: string;
	name: string;
	sku: string;
	/** Optional: a row cached before the Shipping UOM shipped reads back as no unit. */
	unit?: null | RowShippingUnit;
	upc: string;
}

export interface CapturedPhoto {
	image: string;
	mime: string;
	saveItemId?: string;
	saveKey?: string;
	savedItemId?: string;
	savedPhotoId?: string;
}

export type GradeSource = "default" | "grower" | "photo";

/** A single counted item stored per location before posting. */
export interface UnpostedItem {
	batches?: BatchQuantityInput[];
	comment?: string;
	grade: number;
	itemId: string;
	name: string;
	photos?: CapturedPhoto[];
	quantity: number;
	reasonCode?: number;
	reasonText?: string;
	sku: string;
	upc: string;
}

export interface CountDraft {
	batches?: BatchPart[];
	comment: string;
	editingKey?: string;
	grade: null | number;
	gradePhotoId?: string;
	gradeSource?: GradeSource;
	item: CachedItemSummary | null;
	photos?: CapturedPhoto[];
	quantity: number;
	reasonChoice?: ReasonSelection;
}

export interface PendingCountPhoto {
	actionKey: string;
	countActionKey: string;
	input: AttachAvailabilityPhotoInput;
	itemName: string;
}

export interface PostFailureAccepted {
	posted: number;
	queued: number;
}

export interface UnknownCountPost {
	actionKey: string;
	checkedMissing: boolean;
	input: SetInventoryItemInput;
	item: UnpostedItem;
}

export interface ActivityLogSubject {
	itemId: string;
	itemName: string;
	locationId: string;
	locationName: string;
}

export type PendingPatchType = "add" | "adjust" | "move" | "regrade" | "scrap" | "transfer";

export interface PendingPatch {
	actionKey: string;
	activityLogSubject: ActivityLogSubject;
	carriedAllocation?: number;
	inventoryItemId?: string;
	quantity: number;
	recordedBy: string;
	type: PendingPatchType;
	why: string;
}

/** Activity log entry for local history. */
export interface ActivityLogEntry {
	action: "add" | "adjust" | "count" | "move" | "regrade" | "scrap" | "transfer";
	comment?: string;
	itemId: string;
	itemName: string;
	locationId?: string;
	locationName: string;
	quantity: number;
	recordedBy: string;
	timestamp: string;
}

/** Cached transaction for offline history. */
export interface CachedTransaction {
	batchMovements?: BatchView[];
	grade: number;
	id: string;
	itemName: string;
	locationName: string;
	previousQuantity: number;
	quantity: number;
	reasonCode?: string;
	sku: string;
	timestamp: string;
	transactionType: string;
	userId: string;
	why: string;
}

export interface AvailabilityState {
	/** Local activity log (max 500 entries, 7-day cleanup) */
	activityLog: ActivityLogEntry[];
	/** Cached site locations for offline drilldown */
	cachedDrillDownSites?: CachedSiteInfo[];
	/** Cached sub-locations for offline drilldown */
	cachedDrillDownSubLocations?: SubLocationDocument[];
	/** Inventory grid snapshots keyed by locationId — for offline viewing after reload */
	cachedInventoryByLocation: Record<string, InventoryItemRow[]>;
	/** ISO timestamps per location indicating freshness of cached inventory */
	cachedInventoryLastUpdate: Record<string, string>;
	/** Cached items for offline autocomplete */
	cachedItems?: CachedItemSummary[];
	/** Cached locations for offline use (legacy format for move picker) */
	cachedLocations?: AvailabilityLocation[];
	/** Cached transaction history for offline */
	cachedTransactions?: CachedTransaction[];
	draftCounts: Record<string, CountDraft>;
	/** Selected reason-code chips for the history reason filter (multi-select). */
	historyReasonFilter: string[];
	/** Offline status */
	isOffline: boolean;
	/** Last time transaction history was refreshed */
	lastHistoryRefresh?: string;
	pendingCountPhotos?: Record<string, PendingCountPhoto[]>;
	pendingPatches: Record<string, PendingPatch>;
	/** Held post action keys per locationId, keyed by post signature — survives leaving the count screen */
	postActionKeys: Record<string, Record<string, string>>;
	postFailureAccepted?: Record<string, PostFailureAccepted>;
	/** Reason a post was rejected, keyed by locationId */
	postFailures: Record<string, string>;
	postsInFlight?: Record<string, true>;
	/** Currently selected location ID for greenhouse hierarchy */
	selectedLocationId?: string;
	/** Currently selected location display name */
	selectedLocationName?: string;
	/** Full drill-down path for restoring position on "Change" */
	selectedLocationPath?: LocationPathEntry[];
	/** Type of the selected location */
	selectedLocationType?: SiteLocationType | "site";
	/** Site location ID of the selected location */
	selectedSiteLocationId?: string;
	/** Site name of the selected location */
	selectedSiteName?: string;
	/** Unposted counted items keyed by locationId */
	unpostedCounts: Record<string, UnpostedItem[]>;
	unknownCountPosts?: Record<string, UnknownCountPost[]>;
	unsettledMoveTargets?: Record<string, true>;
}

const initialState: AvailabilityState = {
	activityLog: [],
	cachedInventoryByLocation: {},
	cachedInventoryLastUpdate: {},
	cachedItems: [],
	cachedLocations: [],
	cachedTransactions: [],
	draftCounts: {},
	historyReasonFilter: [],
	isOffline: false,
	pendingPatches: {},
	postActionKeys: {},
	postFailures: {},
	postsInFlight: {},
	unpostedCounts: {},
	unknownCountPosts: {},
	unsettledMoveTargets: {}
};

export const resolveActivityLogAction = (patch: PendingPatch): ActivityLogEntry["action"] =>
	patch.type === "adjust" && patch.inventoryItemId === undefined ? "count" : patch.type;

export const availabilitySlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(tenantChanged, (state) => {
			state.selectedLocationId = undefined;
			state.selectedLocationName = undefined;
			state.selectedLocationPath = undefined;
			state.selectedLocationType = undefined;
			state.selectedSiteLocationId = undefined;
			state.selectedSiteName = undefined;
		});
	},
	initialState,
	name: "availability",
	reducers: {
		addActivityLog: (state, action: PayloadAction<ActivityLogEntry>) => {
			if (!state.activityLog) state.activityLog = [];
			state.activityLog.unshift(action.payload);
			if (state.activityLog.length > 500) {
				state.activityLog = state.activityLog.slice(0, 500);
			}
		},
		cleanupActivityLog: (state) => {
			if (!state.activityLog) {
				state.activityLog = [];
				return;
			}
			const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
			state.activityLog = state.activityLog.filter((entry) => entry.timestamp > sevenDaysAgo);
		},
		addPendingCountPhotos: (state, action: PayloadAction<{ locationId: string; photos: PendingCountPhoto[] }>) => {
			const { locationId, photos } = action.payload;
			if (photos.length === 0) return;
			if (!state.pendingCountPhotos) state.pendingCountPhotos = {};
			state.pendingCountPhotos[locationId] = [...(state.pendingCountPhotos[locationId] ?? []), ...photos];
		},
		addPendingPatch: (state, action: PayloadAction<PendingPatch>) => {
			if (!state.pendingPatches) state.pendingPatches = {};
			state.pendingPatches[action.payload.actionKey] = action.payload;
		},
		clearCountDraft: (state, action: PayloadAction<string>) => {
			delete state.draftCounts[action.payload];
		},
		clearHistoryReasonFilter: (state) => {
			state.historyReasonFilter = [];
		},
		clearPostAttempt: (state, action: PayloadAction<string>) => {
			delete state.postActionKeys[action.payload];
			delete state.postFailures[action.payload];
			if (state.postFailureAccepted) delete state.postFailureAccepted[action.payload];
		},
		clearUnpostedCounts: (state, action: PayloadAction<string>) => {
			delete state.unpostedCounts[action.payload];
		},
		commitPendingPatch: (state, action: PayloadAction<string>) => {
			const patch = (state.pendingPatches ?? {})[action.payload];
			if (!patch) return;
			delete state.pendingPatches[action.payload];
			if (!state.activityLog) state.activityLog = [];
			state.activityLog.unshift({
				action: resolveActivityLogAction(patch),
				comment: patch.why,
				itemId: patch.activityLogSubject.itemId,
				itemName: patch.activityLogSubject.itemName,
				locationId: patch.activityLogSubject.locationId,
				locationName: patch.activityLogSubject.locationName,
				quantity: patch.quantity,
				recordedBy: patch.recordedBy,
				timestamp: new Date().toISOString()
			});
			if (state.activityLog.length > 500) {
				state.activityLog = state.activityLog.slice(0, 500);
			}
		},
		discardPendingPatch: (state, action: PayloadAction<string>) => {
			delete (state.pendingPatches ?? {})[action.payload];
		},
		setCachedInventoryForLocation: (
			state,
			action: PayloadAction<{ locationId: string; items: InventoryItemRow[] }>
		) => {
			const { locationId, items } = action.payload;
			if (!state.cachedInventoryByLocation) state.cachedInventoryByLocation = {};
			if (!state.cachedInventoryLastUpdate) state.cachedInventoryLastUpdate = {};
			state.cachedInventoryByLocation[locationId] = items;
			state.cachedInventoryLastUpdate[locationId] = new Date().toISOString();
			if (state.unsettledMoveTargets) delete state.unsettledMoveTargets[locationId];
		},
		setCachedInventoryForLocations: (
			state,
			action: PayloadAction<{ itemsByLocation: Record<string, InventoryItemRow[]> }>
		) => {
			if (!state.cachedInventoryByLocation) state.cachedInventoryByLocation = {};
			if (!state.cachedInventoryLastUpdate) state.cachedInventoryLastUpdate = {};
			const stamp = new Date().toISOString();
			for (const [locationId, items] of Object.entries(action.payload.itemsByLocation)) {
				state.cachedInventoryByLocation[locationId] = items;
				state.cachedInventoryLastUpdate[locationId] = stamp;
				if (state.unsettledMoveTargets) delete state.unsettledMoveTargets[locationId];
			}
		},
		holdUnknownCountPosts: (state, action: PayloadAction<{ locationId: string; posts: UnknownCountPost[] }>) => {
			const { locationId, posts } = action.payload;
			if (!state.unknownCountPosts) state.unknownCountPosts = {};
			const incomingKeys = new Set(posts.map((post) => post.actionKey));
			const kept = (state.unknownCountPosts[locationId] ?? []).filter((post) => !incomingKeys.has(post.actionKey));
			state.unknownCountPosts[locationId] = [...kept, ...posts];
		},
		markUnknownCountMissing: (state, action: PayloadAction<{ actionKey: string; locationId: string }>) => {
			const post = state.unknownCountPosts?.[action.payload.locationId]?.find(
				(row) => row.actionKey === action.payload.actionKey
			);
			if (post) post.checkedMissing = true;
		},
		recordCountPostAccepted: (state, action: PayloadAction<{ locationId: string; posted: number; queued: number }>) => {
			const { locationId, posted, queued } = action.payload;
			if (posted + queued === 0) return;
			const prior = state.postFailureAccepted?.[locationId];
			state.postFailureAccepted = {
				...state.postFailureAccepted,
				[locationId]: { posted: (prior?.posted ?? 0) + posted, queued: (prior?.queued ?? 0) + queued }
			};
		},
		resolveUnknownCountPost: (state, action: PayloadAction<{ actionKey: string; locationId: string }>) => {
			const { actionKey, locationId } = action.payload;
			const unknownPosts = state.unknownCountPosts;
			const posts = unknownPosts?.[locationId];
			if (!unknownPosts || !posts) return;
			unknownPosts[locationId] = posts.filter((post) => post.actionKey !== actionKey);
			const prior = state.postFailureAccepted?.[locationId];
			state.postFailureAccepted = {
				...state.postFailureAccepted,
				[locationId]: { posted: (prior?.posted ?? 0) + 1, queued: prior?.queued ?? 0 }
			};
		},
		markMoveTargetUnsettled: (state, action: PayloadAction<string>) => {
			if (!state.unsettledMoveTargets) state.unsettledMoveTargets = {};
			state.unsettledMoveTargets[action.payload] = true;
		},
		replaceCachedInventoryRows: (state, action: PayloadAction<{ locationId: string; items: InventoryItemRow[] }>) => {
			const { locationId, items } = action.payload;
			if (state.cachedInventoryByLocation?.[locationId]) state.cachedInventoryByLocation[locationId] = items;
		},
		setCachedItems: (state, action: PayloadAction<CachedItemSummary[] | null>) => {
			if (action.payload === null) {
				state.cachedItems = undefined;
				return;
			}
			state.cachedItems = action.payload;
		},
		setCachedLocations: (state, action: PayloadAction<AvailabilityLocation[] | null>) => {
			if (action.payload === null) {
				state.cachedLocations = undefined;
				return;
			}
			state.cachedLocations = action.payload;
		},
		setCachedTransactions: (state, action: PayloadAction<CachedTransaction[] | null>) => {
			state.cachedTransactions = action.payload ?? [];
			state.lastHistoryRefresh = new Date().toISOString();
		},
		setCountDraft: (state, action: PayloadAction<{ draft: CountDraft; locationId: string }>) => {
			state.draftCounts[action.payload.locationId] = action.payload.draft;
		},
		setHistoryReasonFilter: (state, action: PayloadAction<string[] | null>) => {
			state.historyReasonFilter = action.payload ?? [];
		},
		setOfflineStatus: (state, action: PayloadAction<boolean>) => {
			state.isOffline = action.payload;
		},
		setCachedDrillDownData: (
			state,
			action: PayloadAction<{ sites: CachedSiteInfo[]; subLocations: SubLocationDocument[] }>
		) => {
			state.cachedDrillDownSites = action.payload.sites;
			state.cachedDrillDownSubLocations = action.payload.subLocations;
		},
		setPostActionKeys: (
			state,
			action: PayloadAction<{ actionKeysBySignature: Record<string, string>; locationId: string }>
		) => {
			const { actionKeysBySignature, locationId } = action.payload;
			state.postActionKeys[locationId] = actionKeysBySignature;
		},
		setPostFailure: (
			state,
			action: PayloadAction<{ accepted?: PostFailureAccepted; locationId: string; reason: string }>
		) => {
			const { accepted, locationId, reason } = action.payload;
			state.postFailures[locationId] = reason;
			if (!accepted || accepted.posted + accepted.queued === 0) return;
			const prior = state.postFailureAccepted?.[locationId];
			state.postFailureAccepted = {
				...state.postFailureAccepted,
				[locationId]: { posted: (prior?.posted ?? 0) + accepted.posted, queued: (prior?.queued ?? 0) + accepted.queued }
			};
		},
		setPostInFlight: (state, action: PayloadAction<{ inFlight: boolean; locationId: string }>) => {
			const { inFlight, locationId } = action.payload;
			if (!state.postsInFlight) state.postsInFlight = {};
			if (inFlight) state.postsInFlight[locationId] = true;
			else delete state.postsInFlight[locationId];
		},
		setSelectedLocation: (
			state,
			action: PayloadAction<
				| {
						id: string;
						locationPath?: LocationPathEntry[];
						locationType?: SiteLocationType | "site";
						name?: string;
						siteLocationId?: string;
						siteName?: string;
				  }
				| string
				| null
			>
		) => {
			const payload = action.payload;
			if (payload === null) {
				state.selectedLocationId = undefined;
				state.selectedLocationName = undefined;
				state.selectedLocationType = undefined;
				state.selectedSiteLocationId = undefined;
				state.selectedSiteName = undefined;
				return;
			}
			if (typeof payload === "string") {
				state.selectedLocationId = payload;
				return;
			}
			state.selectedLocationId = payload.id;
			state.selectedLocationName = payload.name;
			state.selectedLocationPath = payload.locationPath;
			state.selectedLocationType = payload.locationType;
			state.selectedSiteLocationId = payload.siteLocationId;
			state.selectedSiteName = payload.siteName;
		},
		settlePendingCountPhotos: (state, action: PayloadAction<{ actionKeys: string[]; locationId: string }>) => {
			const { actionKeys, locationId } = action.payload;
			const pending = state.pendingCountPhotos?.[locationId];
			if (!state.pendingCountPhotos || !pending) return;
			const remaining = pending.filter((photo) => !actionKeys.includes(photo.actionKey));
			if (remaining.length > 0) state.pendingCountPhotos[locationId] = remaining;
			else delete state.pendingCountPhotos[locationId];
		},
		setUnpostedCounts: (state, action: PayloadAction<{ items: UnpostedItem[] | null; locationId: string }>) => {
			const { items, locationId } = action.payload;
			if (!items || items.length === 0) {
				delete state.unpostedCounts[locationId];
			} else {
				state.unpostedCounts[locationId] = items;
			}
		},
		toggleHistoryReason: (state, action: PayloadAction<string>) => {
			const code = action.payload;
			const index = state.historyReasonFilter.indexOf(code);
			if (index === -1) {
				state.historyReasonFilter.push(code);
			} else {
				state.historyReasonFilter.splice(index, 1);
			}
		}
	}
});

export const {
	addActivityLog,
	addPendingCountPhotos,
	addPendingPatch,
	cleanupActivityLog,
	clearCountDraft,
	clearHistoryReasonFilter,
	clearPostAttempt,
	clearUnpostedCounts,
	commitPendingPatch,
	discardPendingPatch,
	holdUnknownCountPosts,
	markMoveTargetUnsettled,
	markUnknownCountMissing,
	recordCountPostAccepted,
	resolveUnknownCountPost,
	replaceCachedInventoryRows,
	setCachedDrillDownData,
	setCachedInventoryForLocation,
	setCachedInventoryForLocations,
	setCachedItems,
	setCachedLocations,
	setCachedTransactions,
	setCountDraft,
	setHistoryReasonFilter,
	setOfflineStatus,
	setPostActionKeys,
	setPostFailure,
	setPostInFlight,
	setSelectedLocation,
	setUnpostedCounts,
	settlePendingCountPhotos,
	toggleHistoryReason
} = availabilitySlice.actions;

export default availabilitySlice.reducer;
